<template>
  <div class="input-classic">
    <label
      :class="{
        [status]: status !== 'none',
        'disabled': disabled,
        'filled': value,
        'stacked': stacked,
        'inline': inline,
      }"
    >
      <div
        v-if="label"
        class="label-container"
      >
        <!-- @slot Emplacement pour une icône à gauche du champ label. -->
        <slot name="label-icon" />
        <span
          v-if="!inline || placeholder"
          class="label-text text-medium"
          :class="{ bold: !value, 'no-icon': !$slots['label-icon'], }"
        >
          {{ label }}
        </span>
        <component
          :is="iconFromStatus"
          v-if="!inline"
          class="status-icon"
        />
      </div>
      <div class="input-container">
        <textarea
          v-if="type === 'textarea'"
          v-bind="inputProps"
          @focus="!disabled ? $emit('focus') : ''"
          @blur="!disabled ? $emit('blur') : ''"
          @input="!disabled ? $emit('change', $event.target.value) : ''"
        />
        <input
          v-else
          :type="type"
          v-bind="inputProps"
          @focus="!disabled ? focus($event) : ''"
          @blur="!disabled ? $emit('blur') : ''"
          @input="!disabled ? $emit('change', $event.target.value) : ''"
        />
        <!-- @slot Emplacement pour une icône à droite du champ texte (hors inline). -->
        <slot
          v-if="!inline"
          name="input-icon"
        />
        <component
          :is="iconFromStatus"
          v-if="inline"
          class="status-icon"
        />
      </div>
    </label>

    <p
      v-if="helper"
      class="helper text-small"
    >
      <!-- @slot Emplacement pour une icône à gauche du champ d'aide. -->
      <slot name="helper-icon" />
      {{ helper }}
    </p>
  </div>
</template>

<script>
import { UilAsterisk, UilExclamationCircle, UilCheckCircle } from "@iconscout/vue-unicons";
import IconDownTriangle from "../../components/icons/IconDownTriangle.vue";

/**
 * Affiche un champ input.
 */
export default {
  name: "InputClassic",
  components: {
    UilCheckCircle,
    UilExclamationCircle,
    UilAsterisk,
  },
  model: {
    prop: "value",
    event: "change",
  },
  props: {
    /**
     * Valeur du champ.
     */
    value: {
      type: [String, Number],
      default: "",
    },
    /**
     * Type du champ (text, textarea, number, date...).
     */
    type: {
      type: String,
      default: "text",
    },
    /**
     * Label du champ.
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * Texte indicatif du champ.
     */
    placeholder: {
      type: String,
      default: "",
    },
    /**
     * Longueur minimale du texte de l'input.
     */
    minlength: {
      type: [Number, String],
      default: undefined,
    },
    /**
     * Longueur maximale du texte de l'input.
     */
    maxlength: {
      type: [Number, String],
      default: undefined,
    },
    /**
     * Valeur minimale si l'input est un nombre, une date ou une heure.
     */
    min: {
      type: [Number, String],
      default: undefined,
    },
    /**
     * Valeur maximale si l'input est un nombre, une date ou une heure.
     */
    max: {
      type: [Number, String],
      default: undefined,
    },
    /**
     * Désactive l'input.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Affiche le composant en une ligne.
     */
    inline: {
      type: Boolean,
      default: false,
    },
    /**
     * Variante dédiée au regroupement de ce composant lorsqu'il est affiché plusieurs fois.
     */
    stacked: {
      type: Boolean,
      default: false,
    },
    /**
     * Statut de la valeur dans l'input.
     */
    status: {
      type: String,
      validator: (value) => ["none", "valid", "warning", "error", "required", "locked"].includes(value),
      default: "none",
    },
    /**
     * Ajoute une flèche vers le bas à droite du champs texte pour inline.
     */
    dropdown: {
      type: Boolean,
      default: false,
    },
    /**
     * Place le focus dans le champ.
     */
    autofocus: {
      type: Boolean,
      default: false,
    },
    /**
     * Phrase d'aide sous le composant.
     */
    helper: {
      type: String,
      default: "",
    },
    /**
     * Sélectionne le texte au focus.
     */
    selectOnFocus: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    /**
     * Déclenché au changement du texte.
     * @param {String} - Nouveau texte.
     */
    "change",
    /**
     * Déclenché au changement de valeur de l'input.
     */
    "focus",
    /**
     * Déclenché à la perte du focus de l'input.
     */
    "blur",
    /**
     * Déclenché au changement de valeur de l'input.
     */
    "input",
  ],
  computed: {
    inputProps() {
      return {
        value: this.value,
        class: {
          input: true,
          "label-as-placeholder": !this.placeholder,
          "text-regular": !this.inline,
          "text-medium": this.inline,
        },
        disabled: this.disabled || this.status === "locked",
        required: this.status === "required",
        placeholder: this.inline && !this.placeholder ? this.label : this.placeholder,
        autofocus: this.autofocus,
        minlength: this.type === "text" ? this.minlength : undefined,
        maxlength: this.type === "text" ? this.maxlength : undefined,
        min: ["number", "date", "time"].includes(this.type) ? this.min : undefined,
        max: ["number", "date", "time"].includes(this.type) ? this.max : undefined,
      };
    },
    iconFromStatus() {
      let icon = null;
      if (this.dropdown) {
        return IconDownTriangle;
      }
      switch (this.status) {
        case "required":
          icon = UilAsterisk;
          break;
        case "valid":
          icon = UilCheckCircle;
          break;
        case "warning":
        case "error":
          icon = UilExclamationCircle;
          break;
        default:
          break;
      }
      return icon;
    },
  },
  methods: {
    /**
     * Au focus du champ texte, envoie l'info au parent.
     * @param {Object} event Evenement du input.
     */
    focus(event) {
      if (this.selectOnFocus) {
        event.target.select();
      }
      this.$emit("focus");
    },
  },
};
</script>

<style lang="scss">
@use "../../assets/styles/components/forms/input_classic.scss";
</style>
